import dayjs from "dayjs"
import { Calendar as CalendarIcon } from "iconsax-reactjs"
import * as React from "react"

import { Button } from "@/components/ui"
import { Calendar } from "@/components/ui"
import { Input } from "@/components/ui"
import { Label } from "@/components/ui"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui"
import { cn } from "@/modules/helpers/hooks/shared/utils"

function formatDate(date: Date | undefined) {
  if (!date) {
    return ""
  }

  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false
  }
  return !isNaN(date.getTime())
}

function parseDateString(dateString: string): Date | undefined {
  if (!dateString) return undefined

  // Try to parse with dayjs first (handles most formats automatically)
  const dayjsDate = dayjs(dateString)
  if (dayjsDate.isValid()) {
    return dayjsDate.toDate()
  }

  // Fallback: try manual parsing for DD/MM/YYYY and DD-MM-YYYY
  const parts = dateString.split(/[/-]/)
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1 // Month is 0-indexed
    const year = parseInt(parts[2], 10)
    const date = new Date(year, month, day)
    return isValidDate(date) ? date : undefined
  }

  return undefined
}
interface DatePickerProps {
  value: string | undefined
  onChange: (date: string | undefined) => void
  label?: string
  className?: string
}
export function DatePicker({ value, onChange, label, className }: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(
    value ? parseDateString(value) : undefined
  )
  const [month, setMonth] = React.useState<Date | undefined>(
    value ? parseDateString(value) : undefined
  )

  // Sync with prop value changes
  React.useEffect(() => {
    const newDate = value ? parseDateString(value) : undefined
    setDate(newDate)
    setMonth(newDate)
  }, [value])
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {!!label && (
        <Label htmlFor="date" className="px-1">
          {label}
        </Label>
      )}

      <div className="relative flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="w-full">
              <Input
                id="date"
                value={date ? formatDate(date) : ""}
                placeholder="DD/MM/YYYY"
                className="bg-white pr-10"
                onChange={(e) => {
                  const inputValue = e.target.value
                  if (inputValue === "") {
                    setDate(undefined)
                    onChange(undefined)
                    return
                  }

                  // Parse DD/MM/YYYY format
                  const newDate = parseDateString(inputValue)
                  if (newDate) {
                    setDate(newDate)
                    setMonth(newDate)
                    onChange(formatDate(newDate))
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault()
                    setOpen(true)
                  }
                }}
              />
              <Button
                id="date-picker"
                variant="ghost"
                className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
              >
                <CalendarIcon className="size-5" />
                <span className="sr-only">Chọn ngày</span>
              </Button>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              month={month}
              lang="vi"
              onMonthChange={setMonth}
              onSelect={(selectedDate) => {
                setDate(selectedDate)
                if (selectedDate) {
                  onChange(formatDate(selectedDate))
                } else {
                  onChange(undefined)
                }
                setOpen(false)
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
